import { useEffect, useState } from "react";
import { Col, Row, Label, Form, FormGroup } from "reactstrap";
import { useHistory, useParams } from "react-router-dom";
import { useFormik } from "formik";

import { HeadingWithBackArrow } from "../../../components/HeadingWithBackArrow";
import { LoaderFallbackUI } from "../../../components/LoaderFallbackUI";
import { TextInput } from "../../../components/Input";
import { CustomButton } from "../../../components/CustomButton";
import typography from "../typography";
import {
    getScheduledNotificationById,
    updateScheduledNotification,
} from "../../../api/api.service";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { ConfirmationModal } from "../../../components/CustomModal";
import ModalSuccessImage from "../../../assets/images/app/common/aou-modal-success-img.svg";
import { formatCombinedDateTime } from "../../../utils/dateFormatUtils";
import calendarIcon from "../../../assets/images/app/common/calendar-icon.svg";
import clockIcon from "../../../assets/images/app/common/clock-icon.svg";
import CustomDatePicker from "../../../components/DatePicker/DatePicker";
import { TimePicker } from "../../../components/TimePicker";
import FormikErrorText from "../../../components/FormikErrorText/FormikErrorText";
import { validationSchema } from "../validation";
import { transformApiDataToForm } from "../utils";

export default function EditNotification() {
    const { id } = useParams();
    const history = useHistory();

    const [loading, setLoading] = useState(false);
    const [isModalActive, setIsModalActive] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const [error, setError] = useState(null);

    const backButtonHandler = () => {
        history.push(APP_ROUTES.NOTIFICATION_MANAGEMENT);
    };

    const formik = useFormik({
        initialValues: {
            title: "",
            message: "",
            scheduleDate: "",
            scheduleTime: "",
        },
        validationSchema,
        onSubmit: async (values) => {
            await handleUpdateClick(values);
        },
    });

    const transformFormDataToApi = (formData) => {
        return {
            title: formData.title.trim(),
            message: formData.message.trim(),
            scheduleAt: formatCombinedDateTime(
                formData.scheduleDate,
                formData.scheduleTime
            ),
        };
    };

    useEffect(() => {
        const fetchNotificationData = async () => {
            if (!id) {
                setError("Invalid notification ID");
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const response = await getScheduledNotificationById(id);

                if (response) {
                    const transformedData = transformApiDataToForm(response);
                    formik.setValues(transformedData);
                } else {
                    setError("Notification not found");
                }
            } catch (error) {
                console.error("Error fetching notification:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchNotificationData();
    }, [id]);

    const handleCancel = () => {
        history.push(APP_ROUTES.NOTIFICATION_MANAGEMENT);
    };

    const handleUpdateClick = async (values) => {
        try {
            setIsUpdating(true);
            const requestBody = transformFormDataToApi(values);

            await updateScheduledNotification({
                id: id,
                bodyData: requestBody,
            });

            setIsModalActive(true);
        } catch (error) {
            console.error("Error updating notification:", error);
        } finally {
            setIsUpdating(false);
        }
    };

    const handleModalYes = () => {
        setIsModalActive(false);
        history.push(APP_ROUTES.NOTIFICATION_MANAGEMENT);
    };

    if (loading) {
        return <LoaderFallbackUI isLoader={loading} />;
    }

    if (error) {
        return (
            <div className="error-container" style={{ marginTop: "50px" }}>
                <HeadingWithBackArrow
                    backArrowClickHandler={backButtonHandler}
                    headingTitle={typography.EDIT_NOTIFICATION}
                    showToggle={false}
                />
                <div className="alert alert-danger m-3">
                    <h5>Error</h5>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="edit-notification-heading">
                <HeadingWithBackArrow
                    backArrowClickHandler={backButtonHandler}
                    headingTitle={typography.EDIT_NOTIFICATION}
                    showToggle={false}
                />
                <div className="edit-notification-heading-button">
                    <CustomButton
                        title="Cancel"
                        className="edit-notification-cancel-button"
                        onClick={handleCancel}
                        outline
                        type="button"
                        disabled={isUpdating}
                    />
                    <CustomButton
                        title={isUpdating ? "Updating..." : "Update"}
                        className="edit-notification-update-button"
                        onClick={formik.handleSubmit}
                        type="button"
                        disabled={isUpdating}
                    />
                </div>
            </div>

            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    formik.handleSubmit();
                }}
                className="edit-notification-form"
            >
                <FormGroup>
                    <Label
                        className="edit-notification-form-label"
                        for="notification-title"
                    >
                        {typography.TITLE}
                    </Label>
                    <TextInput
                        id="notification-title"
                        name="title"
                        placeholder="Enter notification title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className="edit-notification-form-input hide-default-icon"
                        disabled={isUpdating}
                    />
                    <FormikErrorText
                        formikInstance={formik}
                        fieldName="title"
                    />
                </FormGroup>

                <FormGroup>
                    <Label
                        className="edit-notification-form-label"
                        for="notification-message"
                    >
                        {typography.NOTIFICATION_MESSAGE}
                    </Label>
                    <textarea
                        id="notification-message"
                        name="message"
                        className="edit-notification-form-textarea"
                        placeholder="Enter notification message"
                        value={formik.values.message}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        rows={6}
                        disabled={isUpdating}
                    />
                    <FormikErrorText
                        formikInstance={formik}
                        fieldName="message"
                    />
                </FormGroup>

                <FormGroup className="edit-notification-form-section">
                    <Row>
                        <Col md="6">
                            <Label
                                className="edit-notification-form-label"
                                for="schedule-date"
                            >
                                {typography.SCHEDULE_DATE}
                                <span className="text-muted"> (UTC)</span>
                            </Label>
                            <div className="edit-notification-input-with-icon">
                                <CustomDatePicker
                                    id="schedule-date"
                                    name="scheduleDate"
                                    placeholder="Select date"
                                    value={formik.values.scheduleDate}
                                    onChange={(val) => {
                                        formik.setFieldValue(
                                            "scheduleDate",
                                            val
                                        );
                                        formik.setFieldTouched(
                                            "scheduleDate",
                                            true
                                        );
                                    }}
                                    className="edit-notification-form-input hide-default-icon"
                                />
                                <img
                                    src={calendarIcon}
                                    alt="calendar"
                                    className="input-icon"
                                />
                            </div>
                            <FormikErrorText
                                formikInstance={formik}
                                fieldName="scheduleDate"
                            />
                        </Col>
                        <Col md="6">
                            <Label
                                className="edit-notification-form-label"
                                for="schedule-time"
                            >
                                {typography.SCHEDULE_TIME}
                                <span className="text-muted"> (UTC)</span>
                            </Label>
                            <div className="edit-notification-input-with-icon">
                                <TimePicker
                                    id="schedule-time"
                                    name="scheduleTime"
                                    placeholder="Select time"
                                    value={formik.values.scheduleTime}
                                    onChange={(val) => {
                                        formik.setFieldValue(
                                            "scheduleTime",
                                            val
                                        );
                                        formik.setFieldTouched(
                                            "scheduleTime",
                                            true
                                        );
                                    }}
                                    className="edit-notification-form-input hide-default-icon"
                                    disabled={isUpdating}
                                />
                                <img
                                    src={clockIcon}
                                    alt="clock"
                                    className="input-icon"
                                />
                            </div>
                            <FormikErrorText
                                formikInstance={formik}
                                fieldName="scheduleTime"
                            />
                        </Col>
                    </Row>
                </FormGroup>
            </Form>

            <ConfirmationModal
                isOpen={isModalActive}
                modalType="SUCCESS"
                onYes={handleModalYes}
                size="md"
                showNoButton={false}
                customImageSrc={ModalSuccessImage}
            />
        </>
    );
}
